import { T, tt } from '@agos/shared'

import { APP_CONTACT_EMAIL } from './brand'
import { GallerySection } from './Gallery'
import {
  BarChart3,
  ChefHat,
  Cloud,
  Gift,
  Receipt,
  Smartphone,
  Sparkles,
  Truck,
} from 'lucide-react'

import {
  FIELDS,
  LISTS,
  anchorOf,
  anyField,
  anyList,
  anyProp,
  bgClass,
  columnsClass,
  headingAlignClass,
  headingClass,
  heroTitleWords,
  panelClass,
  isFeaturedTier,
  monthlyPrice,
  paddingClass,
  prop,
  safeAnchorOrAsset,
  safeAsset,
  styleOf,
  videoEmbed,
  type Section,
} from './site'

/**
 * The nine section types the editor publishes, drawn from the field names the server actually sends.
 *
 * **Every reader here is measured against `__fixtures__/home.json`**, a captured response, because the
 * first version of this file was not. SAAS.md §4.1 and FB-478: it read `ctaLabel` where the payload
 * sends `ctaText`, `items[].text` where it sends `items[].body`, `plans[]` where it sends `tiers[]`
 * and `items[].question` where it sends `items[].q`. Six of nine sections drew a heading and nothing
 * else, and eleven unit tests passed because their fixture was invented in the same sitting.
 *
 * Every field is still free-form JSON somebody typed into the editor, so each reader answers a default
 * rather than rendering `undefined`, and every link and image goes through `safeAsset` first - these
 * are admin-authored and a `javascript:` URL in a logo field is not a logo.
 */
export function SectionView(props: { section: Section }) {
  const { section } = props
  const title = prop(section, 'title')
  const subtitle = prop(section, 'subtitle')
  const eyebrow = anyProp(section, FIELDS.eyebrow)
  const anchor = anchorOf(section)

  // §4.4, FB-489 - the `style` object, which was read by nothing. The §12.3 pair is what showed it:
  // AGOS was left-aligned and body-sized where legacy centres and goes display-sized, because the
  // owner's `{bg: "dark", align: "center", titleSize: "xl"}` was never asked for.
  const style = styleOf(section)
  const body = (
    <section
      className={`px-4 ${paddingClass(style)} ${bgClass(style)}`.trim()}
      data-section={section.type}
      data-section-id={section.id}
      data-section-align={typeof style.align === 'string' ? style.align : 'left'}
    >
      <div
        className={`mx-auto max-w-4xl space-y-3 ${headingAlignClass(section.type, style)} ${panelClass(section.type)}`.trim()}
        data-section-panel={panelClass(section.type) === '' ? undefined : 'true'}
      >
        {/* §4.4 - legacy draws the hero's eyebrow as a pill **above** the title. It lived in the
          * hero body, which `SectionView` renders after the subtitle, so on the pair it sat under the
          * strapline instead of over the headline. Drawn here because that is where it belongs in the
          * order, not because the hero is special. */}
        {eyebrow !== null && (
          <p
            className="inline-block rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] brand-text"
            data-hero-eyebrow
          >
            {eyebrow}
          </p>
        )}
        {title !== null &&
          // §4.4 - the hero is an `<h1>` with its last word in the brand gradient, which is legacy's
          // own deterministic split. Every other section is an `<h2>` at the fixed scale legacy
          // hard-codes for them.
          (section.type === 'hero' ? (
            <h1 className={headingClass(section.type, style)} data-section-title>
              {heroTitleWords(title).map((part, index, all) => (
                <span key={index} className={part.gradient ? 'gradient-text' : undefined}>
                  {part.word}
                  {index < all.length - 1 ? ' ' : ''}
                </span>
              ))}
            </h1>
          ) : (
            <h2 className={headingClass(section.type, style)} data-section-title>
              {title}
            </h2>
          ))}
        {subtitle !== null && (
          // The owner writes these with newlines in them and means them - the hero's subtitle is two
          // sentences on two lines. `whitespace-pre-line` is what keeps that, and without it the page
          // reads as one run-on sentence.
          <p className="whitespace-pre-line text-lg text-slate-400" data-section-subtitle>
            {subtitle}
          </p>
        )}
        <SectionBody section={section} />
      </div>
    </section>
  )

  // Legacy wraps an anchored section so a hero or CTA button can scroll to it. The owner's `cta`
  // section sets `anchorId: "demo"` and their hero button points at `#demo`.
  return anchor === null ? body : <div id={anchor} className="scroll-mt-20">{body}</div>
}

/**
 * The icon a feature card carries - §4.4, FB-489.
 *
 * The payload names a lucide component per item (`ChefHat`, `Truck`, `BarChart3`, …) and nothing read
 * it, so every card lost the tile legacy draws. **Only the eight the owner actually uses are
 * imported**, rather than the whole library: this is a public page and pulling all of lucide into it
 * to satisfy a name nobody has published would be a bundle cost for nothing.
 *
 * An unrecognised name falls back rather than rendering nothing - a card with no tile would be a
 * different shape from the eight beside it, which reads as a broken card rather than as a missing
 * icon.
 */
const FEATURE_ICONS: Record<string, typeof Sparkles> = {
  BarChart3,
  ChefHat,
  Cloud,
  Gift,
  Receipt,
  Smartphone,
  Sparkles,
  Truck,
}

function featureIcon(name: string): typeof Sparkles {
  return FEATURE_ICONS[name] ?? Sparkles
}

/** A button, or nothing. Both CTA slots and both sections that have them come through here. */
/**
 * The button that ends a pricing tier - FB-851, {@code SAAS.md §3.160}.
 *
 * Legacy's is `<a href={`mailto:${APP_CONTACT_EMAIL}`}>Začať</a>`, styled `brand-bg text-white` on the featured
 * plan and `bg-white/5` on the others. AGOS's tier stopped at the module count, so a prospective franchisee read
 * three prices with nowhere to click.
 *
 * <p><b>The label and target are the section's own, with legacy's constant as the fallback.</b> Their stored
 * pricing section carries neither, so a field the Tower can set and nothing defaults would have shipped and
 * changed nothing on the live site - §3.132's defect in reverse.
 *
 * <p>Through {@link safeAnchorOrAsset} rather than the raw value, which is §3.151's guarantee and already accepts
 * `mailto:`. A target it refuses draws **no button**, because a dead-looking control is a smaller fault than a
 * live-looking one that does nothing.
 */
function planAction(props: { label: string | null; href: string | null; featured: boolean }) {
  const { label, featured } = props
  /* The reads happen in the `pricing` case rather than here, which is what `MarketingSectionFieldsAgreeTest`
     asks: it scans a type's own case body for `FIELDS.<key>`, so a field read in a helper reads as a box the
     renderer does not draw. It failed naming `{"pricing"=["ctaText", "ctaHref"]}` on the first draft, and the
     guard's question was the right one. */
  const chosen = props.href
  const href = safeAnchorOrAsset(
    chosen !== null && chosen !== '' ? chosen : `mailto:${APP_CONTACT_EMAIL}`,
  )
  if (href === null) return null
  return (
    <a
      href={href}
      data-plan-cta
      className={
        featured
          ? 'mt-6 block w-full rounded-xl brand-bg py-3 text-center font-bold text-white shadow-lg transition hover:opacity-90'
          : 'mt-6 block w-full rounded-xl bg-white/5 py-3 text-center font-bold transition hover:bg-white/10'
      }
    >
      {label !== null && label !== '' ? label : <T>Začať</T>}
    </a>
  )
}

function Cta(props: { label: string | null; href: string | null; primary: boolean; mark: string }) {
  const { label, href, primary, mark } = props
  /* FB-815, §3.151 - **a label alone is not a button.** This guarded on the label and substituted `#` for a
     missing or unusable target, so a primary CTA could jump to the top of the page and say nothing about it.
     The guard is *has somewhere to go* now.
     An on-page `#features` is a real target and still renders - the owner's own hero uses one. What no longer
     renders is a `#` this code **chose** because it had nothing better, which is a different thing entirely. */
  const safe = href === null ? null : safeAnchorOrAsset(href)
  if (label === null || safe === null) return null
  return (
    <a
      href={safe}
      {...{ [mark]: true }}
      className={
        primary
          ? 'inline-flex min-h-[44px] items-center rounded-xl brand-bg px-4 text-sm font-bold text-white shadow-lg'
          : 'inline-flex min-h-[44px] items-center rounded-xl border border-white/15 bg-white/5 px-4 text-sm font-bold hover:bg-white/10'
      }
    >
      {label}
    </a>
  )
}

function SectionBody({ section }: { section: Section }) {
  switch (section.type) {
    case 'hero': {
      // The eyebrow is drawn by `SectionView`, above the title, which is where legacy puts it.
      const image = anyProp(section, FIELDS.imageUrl)
      const src = image === null ? null : safeAsset(image)
      return (
        <>
          <div className="flex flex-wrap gap-2 justify-inherit">
            <Cta
              label={anyProp(section, FIELDS.ctaText)}
              href={anyProp(section, FIELDS.ctaHref)}
              primary
              mark="data-hero-cta"
            />
            <Cta
              label={anyProp(section, FIELDS.secondaryCtaText)}
              href={anyProp(section, FIELDS.secondaryCtaHref)}
              primary={false}
              mark="data-hero-cta-secondary"
            />
          </div>
          {src !== null && (
            <img src={src} alt="" className="w-full rounded-2xl" data-hero-image loading="eager" />
          )}
        </>
      )
    }

    case 'pricing':
      return (
        <ul className="grid gap-4 text-left sm:grid-cols-3" data-pricing>
          {anyList(section, LISTS.plans).map((plan, index) => {
            const note = anyField(plan, FIELDS.planNote)
            const slug = anyField(plan, ['slug'])
            const featured = isFeaturedTier(styleOf(section), slug, index)
            const modules = plan.moduleCount
            // §4.4 - **this printed `149.00`**: the raw `monthlyPrice` string, on the page that sells
            // the product, with no currency. Legacy prints whole euros and the period beside it.
            const price = monthlyPrice(anyField(plan, FIELDS.planPrice))
            return (
              <li
                key={index}
                data-plan
                data-plan-featured={featured ? 'true' : 'false'}
                className={
                  featured
                    ? 'relative rounded-2xl border border-white/20 bg-gradient-to-b from-white/[0.06] to-transparent p-6 ring-1 ring-white/10'
                    : 'relative rounded-2xl border border-white/10 bg-white/[0.02] p-6'
                }
              >
                {featured && (
                  <span
                    data-plan-badge
                    className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full brand-bg px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white shadow-lg"
                  >
                    <T>Najobľúbenejšie</T>
                  </span>
                )}
                <p className="text-2xl font-bold">{anyField(plan, FIELDS.planName)}</p>
                {note !== '' && (
                  <p className="mt-1 text-sm text-slate-400" data-plan-note>
                    {note}
                  </p>
                )}
                <p className="mt-5" data-plan-price>
                  <span className="text-5xl font-black">{price}</span>
                  <span className="ml-1.5 text-sm text-slate-500">
                    {' / '}
                    <T>mesiac</T>
                  </span>
                </p>
                {typeof modules === 'number' && Number.isFinite(modules) && (
                  <p className="mt-4 text-xs text-slate-500" data-plan-modules>
                    {modules} <T>modulov</T>
                  </p>
                )}
                {/* FB-851, §3.160 - legacy ends every tier with a primary button and AGOS ended at the module
                    count, so the page that sells the product listed three prices and offered nothing to click.
                    The target is the section's own `ctaHref` where the operator set one, and otherwise the
                    ported `APP_CONTACT_EMAIL` - **both halves matter**: their stored section carries no
                    `ctaHref`, so a field with no default would have shipped and changed nothing.
                    Through `safeAnchorOrAsset` for §3.151's guarantee, which already accepts `mailto:`.
                    The featured tier keeps legacy's own styling, because that is the point of
                    `isFeaturedTier` - the highlighted plan's action is the visually primary one. */}
                {planAction({
                  label: anyProp(section, FIELDS.ctaText),
                  href: anyProp(section, FIELDS.ctaHref),
                  featured,
                })}
              </li>
            )
          })}
        </ul>
      )

    case 'faq':
      return (
        <div className="space-y-2 text-left" data-faq>
          {anyList(section, LISTS.items).map((item, index) => (
            // §4.4 - legacy's FAQ is a **collapsed accordion**; AGOS listed every answer, which on a
            // four-question page is a wall of text where legacy shows four lines. `<details>` rather
            // than state: it needs no JavaScript, it is keyboard-operable for nothing, and a crawler
            // still reads the answer inside it - which matters on the page §4.3 is about.
            <details
              key={index}
              data-faq-item
              className="group rounded-xl border border-white/10 bg-white/[0.02] px-4 py-3"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold">
                {anyField(item, FIELDS.question)}
                <span className="text-slate-500 transition-transform group-open:rotate-45">
                  +
                </span>
              </summary>
              <p className="mt-2 text-xs text-slate-400" data-faq-answer>
                {anyField(item, FIELDS.answer)}
              </p>
            </details>
          ))}
        </div>
      )

    case 'testimonials':
      return (
        <ul className="space-y-2" data-testimonials>
          {anyList(section, LISTS.items).map((item, index) => {
            const role = anyField(item, FIELDS.quoteRole)
            return (
              <li key={index} className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-sm" data-testimonial>
                <p data-testimonial-quote>{anyField(item, FIELDS.quote)}</p>
                <p className="mt-1 font-semibold">
                  {anyField(item, FIELDS.quoteAuthor)}
                  {role !== '' && <span className="font-normal text-slate-500"> - {role}</span>}
                </p>
              </li>
            )
          })}
        </ul>
      )

    case 'features':
      return (
        <ul className={`grid gap-3 text-left ${columnsClass(styleOf(section))}`} data-features>
          {anyList(section, LISTS.items).map((item, index) => {
            // §4.4 - `items[].icon` carries a lucide name (`ChefHat`, `Truck`, …) and nothing read it,
            // so every card lost the tile legacy draws. An unknown name gets the fallback rather
            // than nothing: a missing tile would make one card a different shape from its neighbours.
            const Icon = featureIcon(anyField(item, ['icon']))
            return (
              <li
                key={index}
                className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 text-xs"
                data-feature
              >
                <span
                  className="mb-3 inline-flex h-9 w-9 items-center justify-center rounded-xl bg-white/5 brand-text"
                  data-feature-icon={anyField(item, ['icon'])}
                >
                  <Icon className="h-4 w-4" />
                </span>
                <p className="text-sm font-bold">{anyField(item, FIELDS.featureTitle)}</p>
                <p className="mt-1 text-slate-400" data-feature-body>
                  {anyField(item, FIELDS.featureBody)}
                </p>
              </li>
            )
          })}
        </ul>
      )

    // Named logos, which is what the payload holds: a label and a link, no image. The owner's one
    // entry is *Pizza Majstro - Šurany* pointing at their own site. A renderer that insisted on a
    // `src` drew an empty section, which is what it did.
    case 'logos':
      return (
        <ul className="flex flex-wrap items-center gap-3" data-logos>
          {anyList(section, LISTS.items).map((item, index) => {
            const label = anyField(item, FIELDS.logoLabel)
            if (label === '') return null
            const href = anyField(item, FIELDS.logoHref)
            const target = href === '' ? null : safeAnchorOrAsset(href)
            const image = safeAsset(item.src)
            return (
              <li key={index} data-logo>
                {image !== null ? (
                  <img src={image} alt={label} className="h-10 w-auto" data-logo-image />
                ) : target !== null ? (
                  <a
                    href={target}
                    className="text-sm font-semibold underline"
                    rel="noreferrer"
                    data-logo-link
                  >
                    {label}
                  </a>
                ) : (
                  <span className="text-sm font-semibold">{label}</span>
                )}
              </li>
            )
          })}
        </ul>
      )

    case 'cta': {
      /* FB-814, §3.150 - **the e-mail field is deliberately not rendered, because nothing collects it.**
         The form here was `onSubmit={(event) => event.preventDefault()}`: a visitor typed their address,
         pressed Enter and **nothing happened** - no request, no confirmation, no error, address discarded.
         The Tower offers the switch (*Pole na e-mail*), so it was one click from live, and the owner's own
         page already carries `inputPlaceholder: 'test@test.sk'` - somebody has been in that editor.

         **Refusing it is not a parity loss.** Legacy's renderer is the identical `preventDefault`, and it
         puts a button labelled *Odoslať* inside the form - so legacy shows a **Send** button that sends
         nothing. Neither system collects an address. §3.130's precedent argued the same way about legacy's
         `dismissable` label, and this is the stronger case: a label that lies is smaller than a box that
         takes somebody's e-mail and throws it away.

         The `withInput` prop is still read and still settable, so nothing about the stored data changes; what
         changes is that a switched-on field no longer appears. Owner question #89 carries the real thing -
         an endpoint, a table, and a decision about other people's contact details. */
      return (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            <Cta
              label={anyProp(section, FIELDS.ctaText)}
              href={anyProp(section, FIELDS.ctaHref)}
              primary
              mark="data-cta"
            />
            <Cta
              label={anyProp(section, FIELDS.secondaryCtaText)}
              href={anyProp(section, FIELDS.secondaryCtaHref)}
              primary={false}
              mark="data-cta-secondary"
            />
          </div>
        </div>
      )
    }

    case 'footer': {
      const brand = anyProp(section, FIELDS.footerBrand)
      const tagline = anyProp(section, FIELDS.footerTagline)
      const copyright = anyProp(section, FIELDS.footerCopyright)
      const columns = anyList(section, LISTS.columns)
      return (
        <footer className="space-y-3 text-[11px] text-slate-400" data-footer>
          {brand !== null && (
            <p className="text-sm font-bold brand-text" data-footer-brand>
              {brand}
            </p>
          )}
          {tagline !== null && <p data-footer-tagline>{tagline}</p>}
          {columns.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-3" data-footer-columns>
              {columns.map((column, index) => (
                <div key={index} data-footer-column>
                  <p className="font-semibold text-slate-200">{anyField(column, ['title'])}</p>
                  <ul className="space-y-1">
                    {(Array.isArray(column.links) ? (column.links as Record<string, unknown>[]) : []).map(
                      (link, linkIndex) => {
                        const label = anyField(link, FIELDS.linkLabel)
                        if (label === '') return null
                        const href = anyField(link, FIELDS.linkHref)
                        const target = href === '' ? null : safeAnchorOrAsset(href)
                        return (
                          <li key={linkIndex}>
                            {target === null ? (
                              label
                            ) : (
                              <a href={target} className="underline" data-footer-link>
                                {label}
                              </a>
                            )}
                          </li>
                        )
                      },
                    )}
                  </ul>
                </div>
              ))}
            </div>
          )}
          {copyright !== null && <p data-footer-copyright>{copyright}</p>}
        </footer>
      )
    }

    /* §3.131 - text beside an image. **Its paragraph is `body`, read here rather than by the shared chrome**,
       which is §3.129's judgement: `SectionView` reads every section's paragraph from `subtitle`, and aliasing
       that to accept `body` would change how all fifteen other types read theirs to serve one.

       `style.imagePosition === 'left'` reverses the columns, legacy's own key and its own value. A `split` may
       therefore carry both a chrome `subtitle` above and a `body` beside the image, where legacy has only the
       second - a superset, and suppressing the chrome for one type would be a special case in shared code to
       stop an operator doing something reasonable. */
    case 'split': {
      const paragraph = anyProp(section, FIELDS.splitBody)
      const picture = anyProp(section, FIELDS.imageUrl)
      const src = picture === null ? null : safeAsset(picture)
      const reversed = styleOf(section).imagePosition === 'left'
      return (
        <div
          className="grid grid-cols-1 items-center gap-8 text-left lg:grid-cols-2"
          data-split
          data-split-reversed={reversed ? 'true' : 'false'}
        >
          <div className={reversed ? 'lg:order-2' : undefined} data-split-copy>
            {paragraph !== null && (
              <p className="whitespace-pre-line text-muted-foreground" data-split-body>
                {paragraph}
              </p>
            )}
            <div className="mt-5 flex flex-wrap gap-2">
              <Cta
                label={anyProp(section, FIELDS.ctaText)}
                href={anyProp(section, FIELDS.ctaHref)}
                primary
                mark="data-split-cta"
              />
            </div>
          </div>
          {src !== null && (
            <div className={reversed ? 'lg:order-1' : undefined}>
              <img src={src} alt="" className="w-full rounded-2xl border border-border" data-split-image />
            </div>
          )}
        </div>
      )
    }

    /* §3.130 - legacy's strip. **Its message is `text`, not `title`**: legacy's banner has no heading, so the
       shared chrome draws nothing and this is the whole section. `style.tone` is banner-only and carries
       legacy's own four values with its own fallback to `brand`.

       **`dismissible` is deliberately not ported.** Legacy renders the word *dismissable* beside the message and
       provides no control, no state and no storage - a label announcing a capability that does not exist. */
    case 'banner': {
      const message = anyProp(section, FIELDS.bannerText)
      if (message === null) return null
      const tone = styleOf(section).tone
      const toneClass =
        tone === 'dark'
          ? 'bg-slate-800 text-slate-100'
          : tone === 'info'
            ? 'bg-sky-600 text-white'
            : tone === 'success'
              ? 'bg-emerald-600 text-white'
              : 'bg-primary text-primary-foreground'
      return (
        <div
          className={`-mx-4 flex flex-wrap items-center justify-center gap-3 px-5 py-2.5 text-sm ${toneClass}`}
          data-banner
          data-banner-tone={typeof tone === 'string' && tone !== '' ? tone : 'brand'}
        >
          <span className="font-medium whitespace-pre-line" data-banner-text>
            {message}
          </span>
          <Cta
            label={anyProp(section, FIELDS.ctaText)}
            href={anyProp(section, FIELDS.ctaHref)}
            primary={false}
            mark="data-banner-cta"
          />
        </div>
      )
    }

    /* §3.130 - a pull-quote. It reads the same three fields a `testimonials` entry does, which is why they
       needed no new alias, plus legacy's own `avatarUrl`. The quotation marks are legacy's, drawn as text
       rather than as content so a screen reader does not announce them twice. */
    case 'quote': {
      const words = anyProp(section, FIELDS.quote)
      if (words === null) return null
      const author = anyProp(section, FIELDS.quoteAuthor)
      const role = anyProp(section, FIELDS.quoteRole)
      const avatar = anyProp(section, FIELDS.quoteAvatar)
      const face = avatar === null ? null : safeAsset(avatar)
      return (
        <figure data-quote>
          <blockquote
            className="text-2xl sm:text-3xl font-bold leading-tight whitespace-pre-line"
            data-quote-words
          >
            {words}
          </blockquote>
          {(author !== null || face !== null) && (
            <figcaption className="mt-6 flex items-center justify-center gap-3">
              {face !== null && (
                <img src={face} alt="" className="h-12 w-12 rounded-full object-cover" data-quote-avatar />
              )}
              <span className="text-left">
                {author !== null && (
                  <span className="block font-bold" data-quote-author>
                    {author}
                  </span>
                )}
                {role !== null && (
                  <span className="block text-sm text-muted-foreground" data-quote-role>
                    {role}
                  </span>
                )}
              </span>
            </figcaption>
          )}
        </figure>
      )
    }

    /* §3.130 - one image with a caption. `FIELDS.imageUrl` already accepts legacy's `src`, and legacy renders
       nothing at all without one, which `safeAsset` returning null reproduces. */
    case 'image': {
      const source = anyProp(section, FIELDS.imageUrl)
      const src = source === null ? null : safeAsset(source)
      if (src === null) return null
      const caption = anyProp(section, FIELDS.caption)
      const alt = anyProp(section, FIELDS.alt)
      return (
        <figure data-image>
          <img src={src} alt={alt ?? ''} className="w-full rounded-2xl border border-border" />
          {caption !== null && (
            <figcaption className="mt-3 text-center text-sm text-muted-foreground" data-image-caption>
              {caption}
            </figcaption>
          )}
        </figure>
      )
    }

    /* §3.178 - the seventeenth of legacy's eighteen types, and stricter than legacy's by design.
       **A refused URL draws nothing, and the public site says nothing about it** - the same outcome as `image`
       for an unusable `src`, and deliberate: a visitor must not be shown our diagnostics, and the person who can
       fix it is in the Tower, where the field's own label names the two services. That is the one silent outcome
       this entry accepts, because the alternative is a diagnostic on the owner's shopfront.
       **The heading comes from the shared chrome**, so no `title` is read here - which is why `SectionView`
       wraps every case. `referrerPolicy` is ours rather than legacy's: there is no reason to tell the video host
       which page of the owner's site somebody is on. */
    case 'video': {
      const source = anyProp(section, FIELDS.videoUrl)
      const src = source === null ? null : videoEmbed(source)
      if (src === null) return null
      return (
        <div
          className="aspect-video overflow-hidden rounded-2xl border border-border"
          data-video
        >
          <iframe
            src={src}
            title={anyProp(section, FIELDS.featureTitle) ?? tt('Video')}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            className="h-full w-full"
          />
        </div>
      )
    }

    /* §3.130 - controlled blank space, with legacy's five sizes and its own fallback to the middle one.
       `aria-hidden` because there is nothing to announce. */
    case 'spacer': {
      const size = anyProp(section, FIELDS.spacerSize)
      const height =
        size === 'xs' ? 'h-4' : size === 'sm' ? 'h-8' : size === 'lg' ? 'h-32' : size === 'xl' ? 'h-48' : 'h-16'
      return <div className={height} aria-hidden="true" data-spacer={size ?? 'md'} />
    }

    /* §3.129 - a row of figures. **`suffix` is its own field** because legacy draws it at a smaller scale
       beside the number: *1 200* and *+* are one figure and two type sizes, so storing "1 200+" in `value`
       would lose the distinction the design is making. The columns follow the style object, as every other
       list section here does. */
    case 'stats': {
      const figures = anyList(section, LISTS.items)
      /* §3.132 - **legacy derives the column count from how many figures there are**, capped at four:
         `cols = s.columns ?? Math.min(4, (items ?? []).length || 4)`. §3.129 called `columnsClass` with its flat
         default of 3, so a two-figure row drew an empty cell and a four-figure row wrapped 3 + 1. An explicit
         `columns` still wins; this is only what happens when nobody set one. */
      const derived = figures.length === 0 ? 4 : Math.min(4, figures.length)
      return (
        <dl className={`grid gap-6 text-center ${columnsClass(styleOf(section), derived)}`} data-stats>
          {figures.map((item, index) => {
            const value = anyField(item, FIELDS.statValue)
            if (value === '') return null
            const label = anyField(item, FIELDS.statLabel)
            const suffix = anyField(item, FIELDS.statSuffix)
            return (
              <div key={index} data-stat>
                <dd className="text-4xl sm:text-5xl font-black leading-none gradient-text" data-stat-value>
                  {value}
                  {suffix !== '' && <span className="text-2xl sm:text-3xl" data-stat-suffix>{suffix}</span>}
                </dd>
                {label !== '' && (
                  <dt className="mt-2 text-xs uppercase tracking-wider text-muted-foreground" data-stat-label>
                    {label}
                  </dt>
                )}
              </div>
            )
          })}
        </dl>
      )
    }

    /* §3.129 - the numbered how-it-works. **The number is the position, not a field**: legacy draws `i + 1`,
       so an editor box for it would be a figure that can disagree with the order on screen. An `<ol>` for the
       same reason - the order is the meaning. */
    case 'steps':
      return (
        <ol className="grid gap-4 text-left sm:grid-cols-3" data-steps>
          {anyList(section, LISTS.items).map((item, index) => {
            const heading = anyField(item, FIELDS.featureTitle)
            const body = anyField(item, FIELDS.featureBody)
            if (heading === '' && body === '') return null
            return (
              <li
                key={index}
                className="relative rounded-2xl border border-border bg-card/40 p-5 pt-8"
                data-step
              >
                <span
                  className="absolute -top-3 left-5 flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground"
                  data-step-number
                >
                  {index + 1}
                </span>
                {heading !== '' && (
                  <h3 className="font-bold text-base mb-1" data-step-title>
                    {heading}
                  </h3>
                )}
                {body !== '' && (
                  <p className="text-sm text-muted-foreground whitespace-pre-line" data-step-body>
                    {body}
                  </p>
                )}
              </li>
            )
          })}
        </ol>
      )

    // §4.4 - the carousel legacy draws: one featured image with a counter, arrows, a thumbnail strip
    // and a lightbox. Its own file, because it is the only section with state.
    case 'gallery':
      return <GallerySection section={section} />

    // Anything else shaped as a list of images. The owner's `gallery` no longer comes through here;
    // this stays for a type the editor publishes that is shaped the same way.
    default:
      return (
        <div className={`grid gap-2 text-left ${columnsClass(styleOf(section))}`} data-images>
          {anyList(section, LISTS.items).map((item, index) => {
            const src = safeAsset(item.src)
            if (src === null) return null
            const caption = anyField(item, FIELDS.caption)
            return (
              <figure key={index} data-image-figure>
                <img
                  src={src}
                  alt={anyField(item, FIELDS.alt)}
                  className="w-full rounded-xl"
                  data-image
                  loading="lazy"
                />
                {caption !== '' && (
                  <figcaption className="mt-1 text-[11px] text-slate-500" data-image-caption>
                    {caption}
                  </figcaption>
                )}
              </figure>
            )
          })}
        </div>
      )
  }
}

/**
 * Drawn when the site's one read did not answer - FB-825, §3.154.
 *
 * **Not `NotPublished`.** That component says *Táto stránka zatiaľ nie je zverejnená*, which is a positive fact
 * about the owner's business, and `if (isError || page === null)` was telling it to every visitor who arrived during
 * a backend blip. Two states, two sentences.
 *
 * **The retry is the part a visitor can act on**, and the old branch offered nothing of the kind - a public page
 * that says the site does not exist and gives you no way to find out otherwise.
 */
export function SiteUnreachable(props: { note: string; retry: () => void }) {
  return (
    <main className="mx-auto max-w-md p-8 text-center space-y-3" data-site-unreachable>
      <p className="text-sm text-muted-foreground">
        <T>{props.note}</T>
      </p>
      <button
        type="button"
        onClick={props.retry}
        data-site-retry
        className="px-3 py-1.5 rounded-xl border border-white/15 bg-white/5 text-sm font-bold"
      >
        <T>Skúsiť znova</T>
      </button>
    </main>
  )
}

/** Drawn in place of the whole page when there is not one to draw. */
export function NotPublished(props: { note: string }) {
  return (
    <main className="mx-auto max-w-md p-8 text-center" data-not-published>
      <p className="text-sm text-muted-foreground">
        <T>{props.note}</T>
      </p>
    </main>
  )
}
